/**
 * AppLayout Component
 * Main application layout with header for authenticated routes
 */

import { useState, useEffect, useCallback } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { Menu, Search } from 'lucide-react'
import { UserDropdown } from './UserDropdown'
import { Breadcrumb } from './Breadcrumb'
import { BreadcrumbProvider } from './BreadcrumbContext'
import { SidebarOverlay } from './SidebarOverlay'
import { CommandPalette } from './CommandPalette'
import { RealtimeProvider } from '@/providers/RealtimeProvider'
import { useTeamEventInvalidation } from '@/hooks/admin/useAdminTeams'

// UI copy
const translations = {
  logo: 'Triple A',
  openMenu: 'Open menu',
  search: 'Search (Ctrl+K)',
  skipToContent: 'Skip to main content',
}

// Child of RealtimeProvider so the subscription has a connection to listen on.
function LiveTeamData() {
  useTeamEventInvalidation()
  return null
}

export function AppLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false)

  // Handle Cmd+K / Ctrl+K to open command palette
  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    const key = event.key.toLowerCase()
    if ((event.metaKey || event.ctrlKey) && key === 'k') {
      event.preventDefault()
      event.stopPropagation()
      setIsCommandPaletteOpen(true)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown, { capture: true })
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, { capture: true })
    }
  }, [handleGlobalKeyDown])

  const handleMenuClick = () => {
    setIsSidebarOpen(true)
  }

  const handleSearchClick = () => {
    setIsCommandPaletteOpen(true)
  }

  return (
    <div className="min-h-screen bg-background-subtle">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="
          sr-only focus:not-sr-only
          focus:fixed focus:top-4 focus:left-4 focus:z-[100]
          focus:px-4 focus:py-2 focus:bg-primary focus:text-white
          focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-white
        "
      >
        {translations.skipToContent}
      </a>

      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-aaa-dark text-white shadow-md"
        role="banner"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between gap-2 sm:gap-4">
            {/* Left side: Hamburger + Logo */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                type="button"
                onClick={handleMenuClick}
                className="
                  flex h-11 w-11 items-center justify-center rounded-lg
                  text-gray-300 /* categorical: muted text on dark navbar */ hover:bg-white/10 hover:text-white
                  transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-white/50
                "
                aria-label={translations.openMenu}
                aria-expanded={isSidebarOpen}
                aria-controls="sidebar-overlay"
              >
                <Menu className="h-6 w-6" aria-hidden="true" />
              </button>

              <Link
                to="/dashboard"
                className="
                  flex items-center
                  transition-opacity duration-150 hover:opacity-80
                  focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-aaa-dark
                  rounded-md px-1
                "
                aria-label="Triple A - Back to home"
              >
                <img
                  src="/logo-white.png"
                  alt="Triple A Lifestyle Wealth Partner"
                  className="h-7 w-auto"
                />
              </Link>
            </div>

            {/* Right side: Search + User Avatar */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                type="button"
                onClick={handleSearchClick}
                className="
                  flex h-11 w-11 items-center justify-center rounded-lg
                  text-gray-300 /* categorical: muted text on dark navbar */ hover:bg-white/10 hover:text-white
                  transition-colors duration-150
                  focus:outline-none focus:ring-2 focus:ring-white/50
                "
                aria-label={translations.search}
                aria-expanded={isCommandPaletteOpen}
                aria-controls="command-palette"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </button>

              <UserDropdown />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main id="main-content" className="pt-16" role="main">
        <BreadcrumbProvider>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 sm:py-6">
            <Breadcrumb />
            <div className="mt-4">
              <RealtimeProvider>
                <LiveTeamData />
                <Outlet />
              </RealtimeProvider>
            </div>
          </div>
        </BreadcrumbProvider>
      </main>

      {/* Sidebar Overlay */}
      <SidebarOverlay
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </div>
  )
}

export default AppLayout
